import React, {useMemo, useRef} from "react";
import HelpIcon from "../Help/HelpIcon";
import {Bar} from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend as ChartLegend,
  TimeScale,
  ChartOptions,
  ChartData
} from "chart.js";
import "chartjs-adapter-date-fns";

import {chartHeight} from "../../constants";
import {useChartTheme} from "../../contexts/ChartThemeContext";
import {ChartErrorBoundary} from "../Charts/ChartErrorBoundary";
import {createChartDataProcessor, validateChartData} from "../../utils/chartDataProcessor";
import {alignDataToTimeline} from "../../utils/dataAlignment";
import {createTickFormatter} from "../../utils/chartTicks";

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, ChartTooltip, ChartLegend, TimeScale);

interface AntRxFailuresReportProps {
  points: Array<[number, number]>; // [timestamp, rxfail]
  title: string;
  failureRate: number;
  label?: string;
  color: string;
  axisSeries?: {
    name: string;
    data: Array<{timestamp: number; value: number}>;
    timeRange?: {start: number; end: number};
    ticks?: Array<{timestamp: number; label: string}>;
  };
}

const AntRxFailuresReport: React.FC<AntRxFailuresReportProps> = (props) => {
  const chartRef = useRef<ChartJS<"bar", {x: number; y: number}[], unknown>>(null);
  const {getThemeColors} = useChartTheme();
  const dataProcessor = createChartDataProcessor();

  // Create tick formatter with consistent formatting
  // Note: Data is pre-localized in parent ChartsReport, so we pass 0 for timezone offset
  const tickFormatter = useMemo(() => {
    return createTickFormatter(0);
  }, []);

  const chartData = useMemo(() => {
    if (!props.points || props.points.length === 0) {
      // Even with no data, create empty data points for proper time axis display
      if (props.axisSeries?.data && props.axisSeries.data.length > 0) {
        return props.axisSeries.data.map((point) => ({
          time: point.timestamp,
          timestamp: point.timestamp,
          rxfail: 0,
          hasData: false
        }));
      }
      // If no axisSeries, create a minimal dataset for rendering
      // This ensures the chart still displays even with no data
      return [
        {
          time: Date.now(),
          timestamp: Date.now(),
          rxfail: 0,
          hasData: false
        }
      ];
    }

    // If we have axisSeries, use it for proper timeline alignment
    if (props.axisSeries?.data) {
      try {
        // Use 3-second windows with max aggregation for RxFail data
        const alignedData = alignDataToTimeline(
          props.axisSeries.data,
          props.points, // Raw data points [timestamp, value]
          "3s", // 3 second windows
          "max" // Max aggregation for RxFail
        );

        // Convert to chart format
        const chartFormatData = alignedData.map((point) => ({
          time: point.timestamp,
          timestamp: point.timestamp,
          rxfail: point.value || 0,
          hasData: point.value !== null && point.value > 0
        }));

        // Data is already optimally sampled by the preset system
        return chartFormatData;
      } catch {
        // Fall back to original method
      }
    }

    // Fallback: Original processing method (for backward compatibility)
    const validation = validateChartData(
      props.points.map((point) => ({
        timestamp: point[0],
        value: point[1]
      }))
    );

    if (!validation.isValid) {
      return [];
    }

    // Aggregate data into 3-second windows taking max value
    const aggregated = dataProcessor.aggregate(
      props.points.map((point) => ({timestamp: point[0], value: point[1]})),
      3000, // 3 seconds
      "max"
    );

    // Transform to chart format
    const chartData = aggregated.map((item, index) => ({
      time: item.timestamp,
      timestamp: item.timestamp,
      rxfail: item.value,
      hasData: item.value > 0,
      index
    }));

    // Data is already optimally sampled by the preset system
    return chartData;
  }, [props.points, props.axisSeries, dataProcessor]);

  const themeColors = getThemeColors();

  // Prepare data for Chart.js
  const preparedChartData = useMemo(() => {
    const filtered = chartData.filter((d) => d.hasData);

    // If there's no data at all, create a minimal dataset to ensure the chart renders
    if (filtered.length === 0 && chartData.length > 0) {
      // Use the first timestamp point with 0 value to establish the chart
      return [
        {
          x: chartData[0].timestamp,
          y: 0
        }
      ];
    }

    return filtered.map((d) => ({
      x: d.timestamp,
      y: d.rxfail as number
    }));
  }, [chartData]);

  // Chart.js data configuration
  const data: ChartData<"bar", {x: number; y: number}[]> = {
    datasets: [
      {
        label: "RxFail",
        data: preparedChartData,
        backgroundColor: props.color + "CC", // 80% opacity
        borderColor: props.color,
        borderWidth: 1,
        borderRadius: 4,
        barPercentage: 0.9,
        categoryPercentage: 0.8
      }
    ]
  };

  // Chart.js options configuration
  const options: ChartOptions<"bar"> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false, // Disable animations for better performance
      interaction: {
        mode: "index" as const,
        intersect: false
      },
      onClick: (event, activeElements, chart) => {
        // Double click to reset zoom
        const now = Date.now();
        (chart as any).lastClickTime || 0;
        (chart as any).lastClickTime = now;
      },
      plugins: {
        legend: {
          display: false // We show our own label above the chart
        },
        tooltip: {
          enabled: true,
          backgroundColor: themeColors.tooltip.background,
          borderColor: themeColors.tooltip.border,
          borderWidth: 1,
          titleColor: themeColors.tooltip.text,
          bodyColor: themeColors.tooltip.text,
          padding: 12,
          displayColors: true,
          callbacks: {
            title: (context) => {
              const timestamp = context[0]?.parsed?.x;
              return timestamp ? new Date(timestamp).toLocaleTimeString() : "";
            },
            label: (context) => {
              const value = context.parsed?.y;
              return value !== null && value !== undefined ? `RxFail: ${Math.round(value)}` : "RxFail: 0";
            }
          }
        }
      },
      scales: {
        x: {
          type: "linear",
          display: true,
          title: {
            display: false
          },
          grid: {
            display: true,
            color: themeColors.grid + "30",
            drawOnChartArea: true,
            drawTicks: true
          },
          ticks: {
            color: themeColors.text,
            font: {
              size: 11
            },
            maxTicksLimit: 20,
            autoSkip: true,
            autoSkipPadding: 10,
            callback: function (value) {
              return tickFormatter(value);
            }
          },
          min: props.axisSeries?.timeRange?.start,
          max: props.axisSeries?.timeRange?.end
        },
        y: {
          type: "linear" as const,
          display: true,
          position: "left" as const,
          title: {
            display: true,
            text: "RxFail",
            color: themeColors.text
          },
          grid: {
            display: true,
            color: themeColors.grid + "30",
            drawOnChartArea: true,
            drawTicks: true
          },
          ticks: {
            color: themeColors.text,
            font: {
              size: 11
            }
          },
          min: 0
        }
      }
    }),
    [themeColors, props.axisSeries, tickFormatter, preparedChartData]
  );

  const legend = [
    {
      key: "rxfail",
      label: `RxFail - ${props.failureRate}%`
    }
  ];

  return (
    <ChartErrorBoundary>
      <div>
        <h5>
          {props.title}
          <HelpIcon topicId="rxfails" />
        </h5>

        <div style={{marginBottom: "10px"}}>
          <div style={{display: "flex", alignItems: "center", gap: "10px"}}>
            <div
              style={{
                width: "12px",
                height: "12px",
                backgroundColor: props.color,
                borderRadius: "2px"
              }}
            ></div>
            <span style={{color: themeColors.text}}>{legend[0].label}</span>
          </div>
        </div>

        <div style={{width: "100%", height: chartHeight, position: "relative"}}>
          <Bar ref={chartRef} data={data} options={options} />
        </div>
      </div>
    </ChartErrorBoundary>
  );
};

export default AntRxFailuresReport;
